import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ClipboardList,
  Flag,
  HeartPulse,
  LineChart,
  Megaphone,
  Trophy,
  CalendarRange,
  Landmark,
  Network,
  GraduationCap,
  Building2,
  Eye,
  MapPin,
  ArrowRight,
} from "lucide-react";
import { Reveal, Chapter, TrackCorner, EASE } from "./primitives";
import { useLanguage } from "../../i18n/LanguageContext";

const TAB_ICONS = { coaches: ClipboardList, officials: Flag, physiotherapists: HeartPulse, analysts: LineChart, "event-professionals": Megaphone };
const INST_ICONS = [Trophy, CalendarRange, Network, GraduationCap, Building2, Landmark];
const INST_IMAGES = [
  "/images/inst-academy.jpg",
  "/images/inst-tournament.jpg",
  "/images/inst-federation.jpg",
  "/images/inst-school.jpg",
  "/images/inst-venue.jpg",
  "/images/inst-gov.jpg",
];
const GOV_ICONS = [Eye, GraduationCap, MapPin, LineChart];

const ROLE_IMAGES = {
  coaches: "/images/role-coach.jpg",
  officials: "/images/role-official.jpg",
  physiotherapists: "/images/role-physio.jpg",
  analysts: "/images/role-analyst.jpg",
  "event-professionals": "/images/role-event.jpg",
};

export const ForRoles = () => {
  const { t } = useLanguage();
  const R = t.forRoles;
  const [activeId, setActiveId] = useState(R.tabs[0].id);
  const active = R.tabs.find((tab) => tab.id === activeId) || R.tabs[0];

  return (
    <section id="roles" className="relative bg-white py-24 lg:py-32" data-testid="for-roles-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <Chapter index="04" label={R.chapter} />
          <h2 className="mt-6 max-w-2xl text-4xl font-semibold tracking-tight text-kn-navy sm:text-5xl">
            {R.heading}
          </h2>
        </Reveal>

        <div className="mt-14 grid gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-4" delay={0.1}>
            <div className="flex flex-col" role="tablist" data-testid="role-tablist">
              {R.tabs.map((tab) => {
                const Icon = TAB_ICONS[tab.id];
                const isActive = active.id === tab.id;
                return (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={isActive}
                  data-testid={`role-tab-${tab.id}`}
                  onClick={() => setActiveId(tab.id)}
                  className={`group flex items-center justify-between border-b border-kn-navy/10 py-5 text-left transition-colors duration-300 ${
                    isActive ? "text-kn-navy" : "text-kn-navy/40 hover:text-kn-navy/70"
                  }`}
                >
                  <span className="flex items-center gap-4">
                    <Icon
                      className={`h-5 w-5 transition-colors duration-300 ${
                        isActive ? "text-kn-blue" : "text-kn-navy/30 group-hover:text-kn-navy/50"
                      }`}
                      strokeWidth={1.25}
                    />
                    <span className="text-lg font-medium">{tab.title}</span>
                  </span>
                  <ArrowRight
                    className={`h-4 w-4 transition-all duration-300 ${
                      isActive ? "translate-x-0 text-kn-cyan opacity-100" : "-translate-x-2 opacity-0"
                    }`}
                    strokeWidth={1.5}
                  />
                </button>
                );
              })}
            </div>
          </Reveal>

          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={`${active.id}-${R.heading}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="relative h-full overflow-hidden rounded-2xl p-10 lg:p-14"
                style={{ backgroundImage: `linear-gradient(100deg, rgba(4,32,63,0.95) 25%, rgba(4,32,63,0.55)), url(${ROLE_IMAGES[active.id]})`, backgroundSize: "cover", backgroundPosition: "center" }}
                data-testid="role-tab-panel"
              >
                <div className="pointer-events-none absolute -bottom-16 -right-16 text-white/10">
                  <TrackCorner className="h-56 w-56 rotate-180" />
                </div>
                <span className="font-mono text-xs text-kn-cyan">
                  {String(R.tabs.indexOf(active) + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-4 font-serif text-3xl italic text-white">{active.title}</h3>
                <p className="mt-6 max-w-lg text-base leading-relaxed text-white/75">{active.prop}</p>
                <ul className="mt-10 space-y-4">
                  {active.points.map((p) => (
                    <li key={p} className="flex items-center gap-3 border-t border-white/10 pt-4 text-sm text-white/70">
                      <span className="h-1 w-1 rounded-full bg-kn-cyan" />
                      {p}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export const ForInstitutions = () => {
  const { t } = useLanguage();
  const I = t.institutions;
  return (
  <section id="institutions" className="relative bg-kn-ice py-24 lg:py-32" data-testid="for-institutions-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <Chapter index="05" label={I.chapter} />
        <h2 className="mt-6 max-w-3xl text-4xl font-semibold tracking-tight text-kn-navy sm:text-5xl">
          {I.heading}
        </h2>
      </Reveal>

      <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-kn-navy/10 bg-kn-navy/10 sm:grid-cols-2 lg:grid-cols-3">
        {I.cards.map((inst, i) => {
          const Icon = INST_ICONS[i];
          return (
          <motion.div
            key={inst.title}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: i * 0.1, ease: EASE }}
            className="group flex flex-col overflow-hidden bg-white"
            data-testid={`institution-${inst.index}`}
          >
            <div className="relative h-36 overflow-hidden">
              <img
                src={INST_IMAGES[i]}
                alt=""
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-kn-navy/85 via-kn-navy/35 to-kn-navy/10" />
              <div className="absolute bottom-4 left-10 flex items-center gap-3">
                <Icon className="h-7 w-7 text-kn-cyan" strokeWidth={1.25} />
                <span className="font-mono text-xs text-white/70">{inst.index}</span>
              </div>
            </div>
            <div className="flex flex-1 flex-col p-10 pt-8">
            <h3 className="mt-2 text-xl font-semibold text-kn-navy">{inst.title}</h3>
            <p className="mt-3 flex-1 text-sm leading-relaxed text-kn-navy/60">{inst.pain}</p>
            <div className="mt-8 flex flex-wrap gap-2">
              {inst.stages.map((s) => (
                <span key={s} className="rounded-full border border-kn-blue/25 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-kn-blue">
                  {s}
                </span>
              ))}
            </div>
            </div>
          </motion.div>
          );
        })}
      </div>

      <Reveal className="mt-24">
        <div className="bg-tile-gradient relative overflow-hidden rounded-2xl p-10 lg:p-14" data-testid="usecase-block">
          <div className="grid items-start gap-12 lg:grid-cols-2">
            <div>
              <h3 className="font-serif text-3xl italic text-white sm:text-4xl">{I.usecase.h2}</h3>
              <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.28em] text-kn-cyan">{I.usecase.sub}</p>
              <p className="mt-8 max-w-sm font-serif text-2xl italic leading-snug text-white/90">{I.usecase.closing}</p>
            </div>
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl bg-white/10 sm:grid-cols-4">
              {I.usecase.numbers.map((u) => (
                <div key={u.label} className="bg-kn-navy/60 p-5 text-center">
                  <p className="text-3xl font-semibold text-white">{u.n}</p>
                  <p className="mt-1.5 font-mono text-[9px] uppercase tracking-wider text-white/50">{u.label}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-12 border-t border-white/10 pt-8" data-testid="workflow-strip">
            <div className="flex flex-wrap items-center gap-y-4">
              {I.usecase.workflow.map((w, i) => (
                <div key={w} className="flex items-center">
                  <span className="flex items-center gap-3 pr-4">
                    <span className="font-mono text-[10px] text-kn-cyan">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-[13px] font-medium text-white/80">{w}</span>
                  </span>
                  {i < I.usecase.workflow.length - 1 && (
                    <ArrowRight className="mr-4 h-3.5 w-3.5 text-white/25" strokeWidth={1.5} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
  );
};

export const Government = () => {
  const { t } = useLanguage();
  const G = t.government;
  return (
  <section id="government" className="noise relative overflow-hidden bg-kn-navy py-24 lg:py-36" data-testid="government-section">
    <img src="/images/gov-stadium.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />
    <div className="pointer-events-none absolute -left-24 -top-24 text-kn-cyan/15">
      <TrackCorner className="h-[320px] w-[320px] rotate-90" />
    </div>
    <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <Chapter index="06" label={G.chapter} dark />
      </Reveal>
      <div className="mt-10 grid gap-14 lg:grid-cols-2">
        <Reveal delay={0.1}>
          <h2 className="text-4xl font-semibold leading-[1.12] tracking-tight text-white sm:text-5xl">
            {G.h2}
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-white/60">
            {G.body}
          </p>
          <p className="mt-10 border-l-2 border-kn-cyan pl-6 font-serif text-xl italic leading-relaxed text-white/85">
            “{G.quote}”
          </p>
        </Reveal>
        <div className="space-y-px overflow-hidden rounded-xl bg-white/10">
          {G.points.map((g, i) => {
            const Icon = GOV_ICONS[i];
            return (
            <motion.div
              key={g.title}
              initial={{ opacity: 0, x: 16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.1, ease: EASE }}
              className="flex gap-6 bg-kn-navy p-8"
              data-testid={`gov-point-${i}`}
            >
              <Icon className="h-7 w-7 shrink-0 text-kn-cyan" strokeWidth={1} />
              <div>
                <h3 className="text-lg font-semibold text-white">{g.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/55">{g.desc}</p>
              </div>
            </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  </section>
  );
};
